import React, { useState, useRef } from 'react';
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import useClickOutside from '../../../hooks/useClickOutside';
import CustomSelectOptionList from './CustomSelectOptionList';

export interface CustomSelectsOption {
    value: string;
    id: number | string;
    disabled?: boolean;
}

export type SelectVariant = 'outline-orange' | 'outline-green' | 'outline-dark' | 'orange';

interface VariantStyles {
    bg: string;
    selectedBg?: string;
    selectedText: string;
    placeholderText: string;
    border: string;
    selectedBorder?: string;
    hasBorder?: boolean;
    label: string;
    icon: string;
    hover: string;
}

const variantStyles: Record<SelectVariant, VariantStyles> = {
    'outline-orange': {
        bg: '',
        selectedBg: 'bg-brand-orange-10',
        selectedText: 'text-brand-orange-90',
        placeholderText: 'text-system-light-40',
        border: 'border-brand-orange-30',
        selectedBorder: 'border-brand-orange-50',
        hasBorder: true,
        label: 'text-system-light-40',
        icon: 'text-brand-orange-50 w-[1.125rem] h-[1.125rem]',
        hover: 'hover:brightness-110',
    },
    'outline-green': {
        bg: 'bg-primary-100',
        selectedBg: 'bg-brand-green-5',
        selectedText: 'text-secondary-10',
        placeholderText: 'text-system-light-40',
        border: 'border-brand-green-30',
        selectedBorder: 'border-brand-green-20',
        hasBorder: true,
        label: 'text-system-light-40',
        icon: 'text-brand-green-50 w-[1.125rem] h-[1.125rem]',
        hover: 'hover:shadow-custom-dual hover:brightness-95 duration-200',
    },
    'outline-dark': {
        bg: 'transparent',
        selectedBg: 'transparent',
        selectedText: 'text-system-light-40',
        placeholderText: 'text-system-light-40',
        border: 'border-system-light-50',
        selectedBorder: 'border-system-light-50',
        hasBorder: true,
        label: 'text-system-light-40',
        icon: 'text-brand-green-50 w-[1.125rem] h-[1.125rem]',
        hover: 'hover:shadow-custom-dual hover:border-system-light-70 duration-200',
    },
    orange: {
        bg: 'bg-brand-orange-20',
        selectedBg: 'bg-brand-orange-20',
        selectedText: 'text-primary-100',
        placeholderText: 'text-primary-100',
        border: 'border-transparent',
        hasBorder: false,
        label: 'text-system-light-40',
        icon: 'text-primary-100 w-[1.125rem] h-[1.125rem]',
        hover: 'hover:shadow-custom-dual hover:brightness-90 duration-200',
    },
};

export interface ICustomSelects {
    options: CustomSelectsOption[];
    onSelect: (selectedValue: string, label?: string) => void;
    selectedItem: string;
    defaultSelectedItem: string;
    label: string;
    disabled?: boolean
    className?: string
    bgSelect?: string
    variant?: SelectVariant;
    showLabel?: boolean;
}

const CustomSelects: React.FC<ICustomSelects> = ({
    options,
    onSelect,
    selectedItem,
    defaultSelectedItem,
    label,
    disabled,
    className,
    variant = 'orange',
    showLabel = true,
}) => {

    const [isOpen, setIsOpen] = useState(false);
    const CustomSelectsRef = useRef<HTMLDivElement>(null);

    const styles = variantStyles[variant];
    const hoverClass = disabled ? '' : styles.hover;

    const handleClick = (option: CustomSelectsOption) => {
        if (onSelect.length > 1) {
            onSelect(label, option.value,);
        } else {
            onSelect(option.value);
        }
        setIsOpen(false);
    };

    const openCustomSelects = () => {
        if (!disabled) {
            setIsOpen(!isOpen)
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!disabled) {
            if (event.key === 'Escape') {
                setIsOpen(false);
            } else if (event.key === 'ArrowDown' && !isOpen) {
                setIsOpen(true);
            }
        }
    };

    useClickOutside(CustomSelectsRef, () => setIsOpen(false));

    const currentBg = selectedItem ? (styles.selectedBg ?? styles.bg) : styles.bg;
    const currentBorder = selectedItem ? (styles.selectedBorder ?? styles.border) : styles.border;

    return (
        <div
            ref={CustomSelectsRef}
            className={`relative ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className} cursor-pointer`}
            onKeyDown={handleKeyDown}
            onClick={openCustomSelects}
        >
            {showLabel && (
                <p className={`text-lmedium ps-2 pb-1 ${styles.label}`}>
                    {label}
                </p>
            )}
            <div
                className={`h-[32px] flex items-center justify-between gap-1 md:gap-2 px-2 py-1 transition-[filter,background-color,border-color,box-shadow] ease-[cubic-bezier(0.2,0,0,1)] duration-200 rounded-sm ${hoverClass} ${styles.hasBorder
                    ? `border ${currentBorder} ${currentBg}`
                    : currentBg
                    }`}
                tabIndex={isOpen ? 0 : -1}
                aria-labelledby={`${label}-label`}
                aria-expanded={isOpen}
            >
                <div className='flex gap-1 h-full items-center min-w-0'>
                    <span className={`text-llarge truncate w-full ${!selectedItem
                        ? styles.placeholderText
                        : styles.selectedText
                        }`}>
                        {selectedItem || defaultSelectedItem || ''}
                    </span>
                </div>
                <button
                    className='bg-transparent'
                    onClick={(e) => { e.stopPropagation(); openCustomSelects(); }}
                >
                    {
                        isOpen
                            ? < IoMdArrowDropup className={styles.icon} />
                            : <IoMdArrowDropdown className={styles.icon} />
                    }
                </button>
            </div>
            {isOpen && (
                <CustomSelectOptionList
                    options={options}
                    selectedItem={selectedItem}
                    handleClick={handleClick}
                />
            )}
        </div>
    );
};

export default CustomSelects;